import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Info, Download, Pencil, Printer, X, ChevronDown, ChevronUp, BadgeCheck, Star, ArrowRight, Users, AlertTriangle } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';
import Slider from '../shared/Slider';
import IndustryTypeahead from '../shared/IndustryTypeahead';
import ReadyToConnectWidget from '../shared/ReadyToConnectWidget';
import FeaturedPartnerCard from '../shared/FeaturedPartnerCard';
import AnimatedCurrency from '../shared/AnimatedCurrency';
import DataSourcesPanel from './DataSourcesPanel';

function formatCurrency(value) {
  if (!value && value !== 0) return '$0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000).toLocaleString()}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function formatCurrencyFull(value) {
  if (!value && value !== 0) return '$0';
  return `$${Math.round(value).toLocaleString()}`;
}

function StatusDot({ variant = 'orange' }) {
  const colors = { orange: 'hsl(var(--succeedo-orange))', green: 'hsl(var(--chart-2))' };
  return (
    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colors[variant] || colors.orange }} />
  );
}

function CurrencyInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-background border border-input rounded-[8px] pl-8 pr-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
      />
    </div>
  );
}

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex">
      <span onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className="cursor-help">{children}</span>
      {show && <span className="absolute left-6 top-0 z-10 bg-foreground text-background rounded-[8px] p-3 text-xs w-56 shadow-lg">{text}</span>}
    </span>
  );
}

const INDUSTRIES = [
  'Manufacturing', 'Retail', 'Technology', 'Healthcare',
  'Professional Services', 'Construction', 'Hospitality',
  'Food & Beverage', 'Transportation', 'Real Estate',
  'Financial Services', 'Other Services',
];

const SECTOR_MAP = {
  'Manufacturing': 'manufacturing', 'Retail': 'retail', 'Technology': 'technology',
  'Healthcare': 'healthcare', 'Professional Services': 'professional_services',
  'Construction': 'construction', 'Hospitality': 'food_hospitality',
  'Food & Beverage': 'food_hospitality', 'Transportation': 'transportation',
  'Real Estate': 'real_estate', 'Financial Services': 'financial_services',
  'Other Services': 'other',
};

const TIER_STYLES = {
  red_flag: { label: 'RED FLAG', className: 'bg-red-500/10 text-red-500 border-red-500/30' },
  average: { label: 'AVERAGE', className: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  green_zone: { label: 'GREEN ZONE', className: 'bg-green-500/10 text-green-500 border-green-500/30' },
  best_in_class: { label: 'BEST-IN-CLASS', className: 'bg-teal-500/10 text-teal-500 border-teal-500/30' },
};

const BADGE_STYLES = {
  most_reliable: {
    label: 'MOST RELIABLE',
    className: 'text-[hsl(var(--succeedo-orange))] border-[hsl(var(--succeedo-orange))]/30',
    tip: 'For your business shape, this method aligns best with how a buyer would value you.',
  },
  secondary: {
    label: 'SECONDARY',
    className: 'text-muted-foreground border-border',
    tip: 'A useful cross-check, but not the lens we anchor on for your business shape.',
  },
  supporting: {
    label: 'SUPPORTING',
    className: 'text-muted-foreground border-border',
    tip: 'Forward-looking sanity check based on projected cash flows, not current earnings.',
  },
  conservative: {
    label: 'CONSERVATIVE',
    className: 'text-muted-foreground border-border',
    tip: 'A liquidation-style floor — what hard assets alone would fetch. Not a going-concern price.',
  },
  strong_growth: {
    label: 'STRONG GROWTH',
    className: 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30',
    tip: 'High projected growth makes the forward-looking DCF more credible than a static multiple.',
  },
  strong_floor: {
    label: 'STRONG FLOOR',
    className: 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30',
    tip: 'Asset-heavy industry — your tangible asset base sets a meaningful price floor.',
  },
  not_applicable: {
    label: 'N/A',
    className: 'text-muted-foreground/40 border-border/50',
    tip: 'This method does not apply to your current numbers (e.g. negative EBITDA).',
  },
};

// Humanizes benchmark provenance. When the benchmark lookup falls back to the
// generic SMB defaults (no NAICS / sector match), `benchmark_family` arrives as
// "generic_smb_fallback" and `evidence_class` as "broad_fallback" — those are
// internal slugs and shouldn't leak into the seller-facing UI. Returns a single
// human-readable line or null when there's nothing trustworthy to attribute.
function formatEvidence(evidence) {
  if (!evidence) return null;
  const family = evidence.benchmark_family;
  const klass = evidence.evidence_class;
  const isFallback = family === 'generic_smb_fallback' || klass === 'broad_fallback';
  if (isFallback) {
    return 'Industry-agnostic estimate — no direct sector match found';
  }
  const familyLabel = family?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const klassLabel = klass?.replace(/_/g, ' ');
  const date = evidence.source_date;
  return [familyLabel, klassLabel, date].filter(Boolean).join(' · ');
}

function MethodCard({ title, relevance, value, detail, evidence, children }) {
  const badgeKey = relevance?.badge || 'supporting';
  const badgeStyle = BADGE_STYLES[badgeKey] || BADGE_STYLES.supporting;
  const isNA = badgeKey === 'not_applicable';
  const evidenceLine = formatEvidence(evidence);

  return (
    <div className={`bg-muted border border-border rounded-[16px] p-6 transition-all ${isNA ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="font-mono text-[11px] text-muted-foreground uppercase">{title}</div>
        <Tooltip text={badgeStyle.tip}>
          <div className={`font-mono text-[11px] uppercase rounded-full border px-3 py-1 ${badgeStyle.className}`}>
            {badgeStyle.label}
          </div>
        </Tooltip>
      </div>
      <div className={`font-serif font-light text-[32px] mb-1 ${isNA ? 'text-muted-foreground/40' : 'text-foreground'}`}>
        {isNA ? <span>&mdash;</span> : <AnimatedCurrency value={value || 0} />}
      </div>
      {relevance?.reason && (
        <p className="text-[12px] text-foreground/70 mt-2 leading-snug">{relevance.reason}</p>
      )}
      {detail && !isNA && <p className="text-[12px] text-muted-foreground mt-2">{detail}</p>}
      {evidenceLine && !isNA && (
        <p className="font-mono text-[9px] uppercase text-muted-foreground/50 mt-3">Source: {evidenceLine}</p>
      )}
      {children}
    </div>
  );
}

// ============================================================
// Method Spread Card — confidence range + "how we got there"
// ============================================================
// Shows the four valuation methods as a horizontal range bar so the
// 50× spread between asset floor and DCF reads as "different lenses
// across a confidence range," not "the model has no idea." Pairs with
// a collapsible disclosure explaining how the headline number is
// actually computed (EBITDA × CEPA multiple when assessed, else the
// cross-method average) — closes the loop on the most common owner
// confusion: "why doesn't the headline match the most-reliable card?"
function MethodSpreadCard({
  rangeLow, rangeHigh, heroValue, hasAssessment,
  ebitdaMid, revenueMid, dcfMid, assetMid,
  relevance = {}, recastedEbitda, currentMultiple,
}) {
  const [open, setOpen] = useState(false);

  // Position helper: 0% at rangeLow, 100% at rangeHigh.
  const positionFor = (value) => {
    if (!value || rangeHigh <= rangeLow) return 0;
    const clamped = Math.max(rangeLow, Math.min(rangeHigh, value));
    return ((clamped - rangeLow) / (rangeHigh - rangeLow)) * 100;
  };

  const markers = [
    { key: 'asset',   label: 'Asset Floor',     value: assetMid,    badge: relevance.asset?.badge },
    { key: 'revenue', label: 'Revenue × Mult.', value: revenueMid,  badge: relevance.revenue?.badge },
    { key: 'ebitda',  label: 'EBITDA × Mult.',  value: ebitdaMid,   badge: relevance.ebitda?.badge },
    { key: 'dcf',     label: 'DCF',             value: dcfMid,      badge: relevance.dcf?.badge },
  ].filter((m) => m.value && m.value > 0);

  // Sort by horizontal position so we can stagger label rows in
  // position-order. When values bunch (e.g., asset $225K + ebitda $624K
  // both in the bottom 10% of a $225K–$4.5M bar) labels would otherwise
  // collide. Stagger row A/B/A/B by sorted position so adjacent markers
  // never share a row.
  const markersByPosition = [...markers].sort((a, b) => a.value - b.value);
  const rowOffsetForKey = {};
  markersByPosition.forEach((m, idx) => {
    // 56px = label-row A (close to bar), 96px = label-row B (further down).
    rowOffsetForKey[m.key] = idx % 2 === 0 ? 40 : 80;
  });

  const colorForBadge = (badge) => {
    if (badge === 'most_reliable') return 'hsl(var(--succeedo-orange))';
    if (badge === 'strong_growth' || badge === 'strong_floor') return 'hsl(var(--chart-2))';
    if (badge === 'not_applicable') return 'hsl(var(--muted-foreground) / 0.3)';
    return 'hsl(var(--muted-foreground) / 0.6)';
  };

  return (
    <div className="bg-card border border-border rounded-[16px] p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StatusDot variant="orange" />
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            Method Spread
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[12px] flex items-center gap-1 hover:underline"
          style={{ color: 'hsl(var(--succeedo-orange))' }}
        >
          How we got there
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Range bar — bar lives at top: 40px (constant). Method-label rows
          stagger below it (rowOffsetForKey: 56px close, 96px further) so
          adjacent markers can't collide. Container is 156px tall to fit
          the deepest row plus the range endpoints. */}
      <div className="relative px-2" style={{ height: '156px' }}>
        <div className="absolute left-2 right-2 h-[3px] bg-border rounded-full" style={{ top: '40px' }} />
        <div
          className="absolute h-[3px] rounded-full"
          style={{
            top: '40px',
            left: `calc(${positionFor(markers[0]?.value || rangeLow)}% + 8px)`,
            right: `calc(${100 - positionFor(markers[markers.length - 1]?.value || rangeHigh)}% + 8px)`,
            backgroundColor: 'hsl(var(--succeedo-orange) / 0.4)',
          }}
        />

        {/* Method markers — dot is anchored on the bar; leader line drops
            to the staggered label row below. */}
        {markers.map((m) => {
          const pos = positionFor(m.value);
          const rowTop = rowOffsetForKey[m.key];
          // Leader line stretches from bar (top: 40px) down to label row top.
          const leaderTop = 44; // just below the bar
          const leaderHeight = rowTop - leaderTop;
          return (
            <React.Fragment key={m.key}>
              {/* Dot on the bar */}
              <div
                className="absolute"
                style={{ left: `${pos}%`, top: '36px', transform: 'translateX(-50%)' }}
              >
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorForBadge(m.badge) }} />
              </div>
              {/* Leader line from dot to label */}
              {leaderHeight > 0 && (
                <div
                  className="absolute"
                  style={{
                    left: `${pos}%`,
                    top: `${leaderTop}px`,
                    height: `${leaderHeight}px`,
                    width: '1px',
                    backgroundColor: 'hsl(var(--border))',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
              {/* Label + value */}
              <div
                className="absolute"
                style={{ left: `${pos}%`, top: `${rowTop}px`, transform: 'translateX(-50%)' }}
              >
                <div className="font-mono text-[9px] uppercase text-muted-foreground/70 whitespace-nowrap text-center">
                  {m.label}
                </div>
                <div className="text-[11px] font-medium text-foreground/80 mt-0.5 text-center whitespace-nowrap">
                  {formatCurrency(m.value)}
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {/* Headline marker — sits above the bar, separate from the
            staggered method rows so it never collides with them. */}
        {heroValue > 0 && (
          <div
            className="absolute"
            style={{ left: `${positionFor(heroValue)}%`, top: 0, transform: 'translateX(-50%)' }}
          >
            <div className="font-mono text-[9px] uppercase tracking-wide text-center" style={{ color: 'hsl(var(--succeedo-orange))' }}>
              Headline
            </div>
            <div className="text-[12px] font-semibold text-center mt-0.5" style={{ color: 'hsl(var(--succeedo-orange))' }}>
              {formatCurrency(heroValue)}
            </div>
            <div className="w-px h-3 mx-auto mt-1" style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }} />
          </div>
        )}

        {/* Range endpoints */}
        <div className="absolute bottom-0 left-2 text-[10px] font-mono text-muted-foreground/60">
          {formatCurrency(rangeLow)}
        </div>
        <div className="absolute bottom-0 right-2 text-[10px] font-mono text-muted-foreground/60">
          {formatCurrency(rangeHigh)}
        </div>
      </div>

      {/* Disclosure */}
      {open && (
        <div className="border-t border-border pt-4 mt-4 text-[13px] text-foreground/80 leading-relaxed space-y-3">
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground/60 tracking-wide mb-1">
              How the headline is computed
            </div>
            {hasAssessment && recastedEbitda && currentMultiple ? (
              <p>
                <span className="font-medium">Recasted EBITDA</span> ({formatCurrency(recastedEbitda)})
                {' × '}
                <span className="font-medium">Common-Sense-adjusted multiple</span> ({Number(currentMultiple).toFixed(1)}×)
                {' = '}
                <span className="font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                  {formatCurrency(heroValue)}
                </span>.
                The multiple is positioned within your industry&apos;s range based on your assessment
                score — a higher score earns a higher multiple. The four methods below are
                alternative lenses we cross-check against this number.
              </p>
            ) : (
              <p>
                Without an assessment score, we report a range across the four methods rather than a
                single headline. Complete your business readiness assessment to anchor on a CEPA-adjusted
                figure.
              </p>
            )}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground/60 tracking-wide mb-1">
              Why the methods spread so widely
            </div>
            <p>
              Each lens captures a different facet — current earnings (EBITDA × Multiple), top-line
              quality (Revenue × Multiple), forward cash flows (DCF), and liquidation floor
              (Asset-Based). For most going-concern SMBs the asset floor is far below the market
              price, and the DCF can run high when growth assumptions are aggressive. A real
              transaction usually lands inside the range above — closer to the highlighted lens.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Edit Inputs Modal
// ============================================================
function EditInputsModal({ inputs, onClose, onRecalculate }) {
  // Backend returns snake_case keys — normalize for both conventions
  const sectorCluster = inputs.sector_cluster || inputs.sectorCluster || '';
  const matchedIndustry = Object.entries(SECTOR_MAP).find(([, v]) => v === sectorCluster)?.[0] || 'Professional Services';

  const [industry, setIndustry] = useState(matchedIndustry);
  const [selectedNaics, setSelectedNaics] = useState(null);
  const [revenue, setRevenue] = useState(String(inputs.revenue || ''));
  const [expenses, setExpenses] = useState(String(inputs.operating_expenses || inputs.operatingExpenses || ''));
  const [recurringPct, setRecurringPct] = useState(Math.round((inputs.recurring_revenue_pct || inputs.recurringRevenuePct || 0) * 100));
  const adj = inputs.adjustments || {};
  const [ownerComp, setOwnerComp] = useState(String(adj.owner_excess_comp || 0));
  const [oneTime, setOneTime] = useState(String(adj.one_time_expenses || 0));
  const [nonOp, setNonOp] = useState(String(adj.non_operating_income || 0));
  const [other, setOther] = useState(String(adj.other_adjustments || 0));
  // SDE inputs — round-trip from saved snapshots (snake or camel) so that
  // re-opening the editor preserves the prior owner-operated state.
  const [isOwnerOperated, setIsOwnerOperated] = useState(
    inputs.is_owner_operated ?? inputs.isOwnerOperated ?? false,
  );
  const [ownerTotalComp, setOwnerTotalComp] = useState(
    String(inputs.owner_total_comp || inputs.ownerTotalComp || 0),
  );
  const [assets, setAssets] = useState(String(inputs.tangible_assets || inputs.tangibleAssets || 0));
  const [liabilities, setLiabilities] = useState(String(inputs.liabilities || 0));
  const growthSrc = inputs.growth_source || inputs.growthSource || '';
  const [growthMode, setGrowthMode] = useState(growthSrc === 'historical_cagr' ? 'calculate' : 'known');
  const rawGrowth = inputs.growth_rate || inputs.growthRate || 0;
  const [growthRate, setGrowthRate] = useState(String(Math.round(rawGrowth * 100)));

  const parseNum = (v) => parseFloat(String(v).replace(/,/g, '')) || 0;
  const reportedEBITDA = parseNum(revenue) - parseNum(expenses);
  const totalAddBacks = parseNum(ownerComp) + parseNum(oneTime) + parseNum(nonOp) + parseNum(other);
  const recastedEBITDA = reportedEBITDA + totalAddBacks;

  const handleSubmit = () => {
    // SDE inputs are top-level (not nested in adjustments) so the container's
    // GraphQL mutations can pass them as first-class args (`is_owner_operated`,
    // `owner_total_comp`) on `create_smb_valuation` / `save_valuation_snapshot`.
    // The server validates owner_total_comp at the mutation boundary
    // (non-negative, finite, ≤ $5M CAD).
    onRecalculate({
      revenue: parseNum(revenue),
      operatingExpenses: parseNum(expenses),
      recurringRevenuePct: recurringPct / 100,
      adjustments: {
        owner_excess_comp: parseNum(ownerComp),
        one_time_expenses: parseNum(oneTime),
        non_operating_income: parseNum(nonOp),
        other_adjustments: parseNum(other),
      },
      isOwnerOperated: isOwnerOperated,
      ownerTotalComp: parseNum(ownerTotalComp),
      growthRate: growthMode === 'known' ? parseNum(growthRate) / 100 : null,
      tangibleAssets: parseNum(assets),
      liabilities: parseNum(liabilities),
      industryNaicsCode: selectedNaics?.naicsCode || null,
      sectorCluster: selectedNaics?.sectorCluster || SECTOR_MAP[industry] || 'other',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[16px] p-8 max-w-[680px] w-full max-h-[90vh] overflow-y-auto shadow-2xl relative">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        <h2 className="font-serif text-[28px] font-light text-foreground mb-6">Edit Valuation Inputs</h2>

        {/* Industry */}
        <div className="mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">INDUSTRY</div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {INDUSTRIES.map((ind) => (
              <button key={ind} type="button" onClick={() => { setIndustry(ind); setSelectedNaics(null); }}
                className={`px-3 py-2 rounded-[8px] text-[13px] font-medium transition-all ${industry === ind && !selectedNaics ? 'bg-foreground text-background' : 'bg-background border border-input text-foreground hover:border-[hsl(var(--succeedo-orange))]'}`}>
                {ind}
              </button>
            ))}
          </div>
          <label className="block text-[13px] text-muted-foreground mb-2">Or search for a specific industry</label>
          <IndustryTypeahead
            onSelect={(item) => { setSelectedNaics(item); setIndustry(''); }}
          />
          {selectedNaics && (
            <div className="bg-background border border-border rounded-[8px] p-3 mt-3">
              <p className="text-[12px] text-muted-foreground">
                Using <span className="font-medium text-foreground">{selectedNaics.benchmarkFamily}</span> for {selectedNaics.industryName} (NAICS {selectedNaics.naicsCode})
              </p>
            </div>
          )}
        </div>

        {/* Revenue */}
        <div className="border-t border-border pt-6 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">REVENUE</div>
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] text-muted-foreground mb-1">Annual Revenue</label>
              <CurrencyInput value={revenue} onChange={setRevenue} placeholder="2,500,000" />
            </div>
            <div>
              <label className="block text-[13px] text-muted-foreground mb-2">Recurring Revenue %</label>
              <Slider min={0} max={100} step={1} value={recurringPct} onChange={setRecurringPct} showValue formatValue={(v) => `${v}%`} />
            </div>
            <div>
              <label className="block text-[13px] text-muted-foreground mb-2">Growth Rate</label>
              <div className="flex gap-3 mb-3">
                <button type="button" onClick={() => setGrowthMode('known')} className={`flex-1 px-3 py-2 rounded-[8px] text-[13px] font-medium ${growthMode === 'known' ? 'bg-foreground text-background' : 'bg-background border border-input'}`}>I know my rate</button>
                <button type="button" onClick={() => setGrowthMode('calculate')} className={`flex-1 px-3 py-2 rounded-[8px] text-[13px] font-medium ${growthMode === 'calculate' ? 'bg-foreground text-background' : 'bg-background border border-input'}`}>From history</button>
              </div>
              {growthMode === 'known' && (
                <div className="relative">
                  <input type="number" value={growthRate} onChange={(e) => setGrowthRate(e.target.value)} placeholder="10" className="w-full bg-background border border-input rounded-[8px] px-4 py-2 pr-10 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Owner-operated toggle (SDE method gate) */}
        <div className="border-t border-border pt-6 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">OWNERSHIP</div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-medium text-foreground mb-1">
                Owner-operated business?
              </div>
              <div className="text-[12px] text-muted-foreground leading-snug">
                Unlocks the SDE × Multiple lens for Main Street businesses where one full-time owner
                is the centre of the enterprise.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOwnerOperated((v) => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mt-1 ${
                isOwnerOperated ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-border'
              }`}
              aria-label="Toggle owner-operated"
              aria-pressed={isOwnerOperated}
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                isOwnerOperated ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          {isOwnerOperated && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-[12px] text-muted-foreground">Owner&apos;s total annual compensation</label>
                <Tooltip text="Total: salary + benefits + dividends. Distinct from 'Owner excess compensation' below — that's only the above-market portion.">
                  <Info className="w-3.5 h-3.5 text-muted-foreground" />
                </Tooltip>
              </div>
              <CurrencyInput value={ownerTotalComp} onChange={setOwnerTotalComp} placeholder="80,000" />
            </div>
          )}
        </div>

        {/* Expenses + Recasting */}
        <div className="border-t border-border pt-6 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">EXPENSES &amp; RECASTING</div>
          <div className="space-y-3">
            <div>
              <label className="block text-[13px] text-muted-foreground mb-1">Operating Expenses</label>
              <CurrencyInput value={expenses} onChange={setExpenses} placeholder="2,000,000" />
              <p className="text-[12px] text-muted-foreground/70 mt-2 leading-relaxed">
                Enter all expenses as reported on your P&amp;L, including your full owner salary.
                We&apos;ll subtract the above-market portion separately as an add-back below — entering a
                pre-adjusted number here will double-count it.
              </p>
            </div>
            {[['Owner excess compensation', ownerComp, setOwnerComp, 'Amount above market rate for your role'],
              ['One-time expenses', oneTime, setOneTime, 'Non-recurring costs'],
              ['Non-operating income', nonOp, setNonOp, 'Income not from core operations'],
              ['Other adjustments', other, setOther, 'Other non-recurring expenses']].map(([label, val, setter, tip]) => (
              <div key={label}>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-[12px] text-muted-foreground">{label}</label>
                  <Tooltip text={tip}><Info className="w-3.5 h-3.5 text-muted-foreground" /></Tooltip>
                </div>
                <CurrencyInput value={val} onChange={setter} placeholder="0" />
              </div>
            ))}
            {(reportedEBITDA !== 0 || totalAddBacks > 0) && (
              <div className="bg-muted border border-border rounded-[8px] p-3 flex items-center gap-2 flex-wrap text-[12px]">
                <span className="text-muted-foreground">Reported:</span>
                <span className="font-semibold text-foreground">${reportedEBITDA.toLocaleString()}</span>
                <span style={{ color: 'hsl(var(--succeedo-orange))' }}>&rarr;</span>
                <span className="text-muted-foreground">Recasted:</span>
                <span className="font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>${recastedEBITDA.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Assets */}
        <div className="border-t border-border pt-6 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">ASSETS (OPTIONAL)</div>
          <div className="space-y-3">
            <div>
              <label className="block text-[13px] text-muted-foreground mb-1">Tangible Assets</label>
              <CurrencyInput value={assets} onChange={setAssets} placeholder="500,000" />
            </div>
            <div>
              <label className="block text-[13px] text-muted-foreground mb-1">Liabilities</label>
              <CurrencyInput value={liabilities} onChange={setLiabilities} placeholder="200,000" />
            </div>
          </div>
        </div>

        <button type="button" onClick={handleSubmit} className="w-full bg-foreground text-background rounded-full px-6 py-3 font-medium hover:bg-foreground/90 transition-colors">
          Recalculate
        </button>
        <div className="text-center mt-3">
          <button type="button" onClick={onClose} className="text-muted-foreground text-sm hover:text-foreground">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DCF Projection Modal
// ============================================================
function DCFProjectionModal({ dcf, onClose }) {
  if (!dcf) return null;
  const {
    projections = [], wacc, wacc_components,
    terminal_value, pv_terminal, enterprise_value,
    growth_rate, growth_source,
    terminal_growth_rate, expense_inflation_rate, tax_rate,
  } = dcf;
  const fmtPct = (v, digits = 1) => (v == null ? '—' : `${(v * 100).toFixed(digits)}%`);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[16px] p-8 max-w-[800px] w-full max-h-[90vh] overflow-y-auto shadow-2xl relative">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        <h2 className="font-serif text-[28px] font-light text-foreground mb-6">DCF Projection</h2>

        {/* WACC Breakdown */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <StatusDot variant="orange" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">DISCOUNT RATE</span>
          </div>
          <div className="space-y-2 text-[13px]">
            {wacc_components && Object.entries(wacc_components).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span className="text-muted-foreground">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                <span className="text-foreground">{(val * 100).toFixed(1)}%</span>
              </div>
            ))}
            <div className="border-t border-border pt-2 flex justify-between font-semibold">
              <span className="text-foreground">Total WACC</span>
              <span className="text-foreground">{wacc ? (wacc * 100).toFixed(1) : 0}%</span>
            </div>
          </div>
          <div className="text-[13px] text-muted-foreground mt-3 space-y-1">
            <div>
              <span className="font-medium">5-year revenue growth:</span> {fmtPct(growth_rate)}
              {' '}({growth_source === 'historical_cagr' ? 'historical CAGR' : growth_source === 'manual' ? 'manual input' : 'default'})
            </div>
            <div>
              <span className="font-medium">Terminal growth:</span> {fmtPct(terminal_growth_rate)}
              {' '}<span className="text-muted-foreground/70">(long-run inflation proxy applied to perpetuity)</span>
            </div>
            <div>
              <span className="font-medium">Operating expense inflation:</span> {fmtPct(expense_inflation_rate)} per year
            </div>
            <div>
              <span className="font-medium">Corporate tax (Y1 effective):</span> {fmtPct(tax_rate)}
              {' '}<span className="text-muted-foreground/70">
                (small-business deduction up to $500K, general rate above —
                effective rate rises in later years as projected EBITDA crosses the threshold).
              </span>
            </div>
          </div>
        </div>

        {/* Projection Table */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <StatusDot variant="orange" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">5-YEAR PROJECTION</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-muted">
                  {['Year', 'Revenue', 'Expenses', 'EBITDA', 'Tax', 'FCF', 'Discount', 'PV'].map((h) => (
                    <th key={h} className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide px-3 py-2 text-right first:text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projections.map((p, i) => (
                  <tr key={p.year} className={i % 2 === 0 ? '' : 'bg-muted/30'}>
                    <td className="px-3 py-2 text-foreground">{p.year}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.revenue)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.expenses)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.ebitda)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.tax)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.fcf)}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{p.discount_factor?.toFixed(3)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{formatCurrency(p.pv_fcf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Terminal Value */}
          <div className="border-t border-border mt-4 pt-4 space-y-2 text-[13px]">
            <div className="flex justify-between"><span className="text-muted-foreground">Terminal Value</span><span className="text-foreground font-semibold">{formatCurrency(terminal_value)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">PV of Terminal Value</span><span className="text-foreground font-semibold">{formatCurrency(pv_terminal)}</span></div>
            <div className="flex justify-between border-t border-border pt-2">
              <span className="text-foreground font-semibold">Total Enterprise Value</span>
              <span className="text-[18px] font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>{formatCurrency(enterprise_value)}</span>
            </div>
          </div>

          {/* Methodology footnote: this is a simplified DCF aimed at SMB
              sellers. A formal buy-side model would also subtract changes
              in working capital and capital expenditures from NOPAT to get
              FCF; we omit those because most SMBs don't track them. */}
          <p className="text-[11px] text-muted-foreground/70 mt-4 leading-relaxed">
            <span className="font-medium">Note:</span> Free Cash Flow here is computed as NOPAT (EBITDA − tax).
            This simplified DCF omits changes in working capital and capital expenditures, which a formal
            buy-side model would subtract from NOPAT. Treat this as a directional estimate — a CBV-led
            valuation will refine it further.
          </p>
        </div>

        <div className="flex justify-center gap-4">
          <button type="button" onClick={() => window.print()} className="border border-border text-foreground rounded-full px-5 py-2 text-sm flex items-center gap-2 hover:bg-accent transition-colors">
            <Printer className="w-4 h-4" /> Print Projection
          </button>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Results Component
// ============================================================
function ValuationSnapshotResult({
  result,
  partnerSuggestions,
  savedSnapshots,
  onBack,
  onRecalculate,
  onSaveSnapshot,
  onLoadSnapshot,
  onRequestIntroduction,
  introRequests,
  featuredPartners,
  introLoading,
  isPrimary,
  onSetPrimary,
  readOnly,
  bare,
  dealTeam,
  onShareWithTeam,
}) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDCFModal, setShowDCFModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [valueGapExpanded, setValueGapExpanded] = useState(false);

  if (!result) return null;

  const { sde_method, ebitda_method, revenue_method, dcf_method, asset_method, overall_range, method_relevance, benchmark_info, inputs, value_acceleration, data_sources } = result;

  // Fallback: compute relevance client-side if not in result (older saved snapshots)
  const rel = (() => {
    if (method_relevance && Object.keys(method_relevance).length > 0) return method_relevance;
    const ebitdaVal = ebitda_method?.adjusted_ebitda || ebitda_method?.reported_ebitda || 0;
    const ebitdaPositive = ebitdaVal > 0;
    const recurringPct = revenue_method?.recurring_pct || inputs?.recurring_revenue_pct || 0;
    const growthRate = dcf_method?.growth_rate || 0;
    const highRecurring = recurringPct >= 0.70;
    const highGrowth = growthRate >= 0.15;

    if (!ebitdaPositive && highGrowth) {
      return { ebitda: { badge: 'not_applicable', reason: 'Negative EBITDA' }, revenue: { badge: 'most_reliable', reason: 'Best indicator for pre-profit businesses' }, dcf: { badge: 'strong_growth', reason: 'High growth supports DCF' }, asset: { badge: 'conservative', reason: 'Net asset floor' } };
    } else if (!ebitdaPositive) {
      return { ebitda: { badge: 'not_applicable', reason: 'Negative EBITDA' }, revenue: { badge: 'most_reliable', reason: 'Best indicator when EBITDA is negative' }, dcf: { badge: 'supporting', reason: 'Cash flow projection' }, asset: { badge: 'conservative', reason: 'Net asset floor' } };
    } else if (highRecurring) {
      return { ebitda: { badge: 'secondary', reason: 'Validates recurring revenue premium' }, revenue: { badge: 'most_reliable', reason: `High recurring revenue (${Math.round(recurringPct * 100)}%)` }, dcf: { badge: 'supporting', reason: 'Predictable cash flows' }, asset: { badge: 'conservative', reason: 'Net asset floor' } };
    } else if (highGrowth) {
      return { ebitda: { badge: 'most_reliable', reason: 'Industry standard' }, revenue: { badge: 'secondary', reason: 'Validates EBITDA' }, dcf: { badge: 'strong_growth', reason: `High growth (${Math.round(growthRate * 100)}%)` }, asset: { badge: 'conservative', reason: 'Net asset floor' } };
    }
    return { ebitda: { badge: 'most_reliable', reason: 'Industry standard for operating businesses' }, revenue: { badge: 'secondary', reason: 'Validates EBITDA estimate' }, dcf: { badge: 'supporting', reason: 'Cash flow projection' }, asset: { badge: 'conservative', reason: 'Net asset floor' } };
  })();

  const rangeLow = overall_range?.low || 0;
  const rangeHigh = overall_range?.high || 0;

  // Derive hero value: CEPA-adjusted current_value when present, fallback to overall_range
  const va = value_acceleration;
  const hasAssessment = !!(va && va.current_value);
  const heroValue = hasAssessment ? va.current_value : null;

  // Normalize assessment score: backend returns 0.0-1.0
  const rawScore = va?.assessment_score || 0;
  const assessmentScorePct = Math.round(rawScore <= 1 ? rawScore * 100 : rawScore);

  // Tier info
  const tierKey = va?.tier || '';
  const tierStyle = TIER_STYLES[tierKey] || TIER_STYLES.average;

  // Value gap range slider position
  const calculateSliderPosition = () => {
    const currentMultiple = va?.current_multiple || 0;
    const rLow = va?.range_low || 0;
    const rHigh = va?.range_high || 0;
    if (!currentMultiple || !rLow || !rHigh || rHigh === rLow) return 50;
    const position = ((currentMultiple - rLow) / (rHigh - rLow)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const [lastSavedSnapshotId, setLastSavedSnapshotId] = useState(null);

  const handleSaveSnapshot = async () => {
    setSaveStatus('saving');
    const snapshot = onSaveSnapshot ? await onSaveSnapshot() : null;
    if (snapshot?.id) {
      setLastSavedSnapshotId(snapshot.id);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } else {
      setSaveStatus('idle');
    }
  };

  const handleExportPDF = async () => {
    let snapshotId = lastSavedSnapshotId;

    if (!snapshotId) {
      setSaveStatus('saving');
      const snapshot = onSaveSnapshot ? await onSaveSnapshot() : null;
      if (snapshot?.id) {
        snapshotId = snapshot.id;
        setLastSavedSnapshotId(snapshotId);
        setSaveStatus('idle');
      } else {
        setSaveStatus('idle');
        return;
      }
    }

    window.open(`/succeedo/valuation/${snapshotId}/pdf`, '_blank');
  };

  const handleRecalculate = (updatedInputs) => {
    if (onRecalculate) onRecalculate(updatedInputs);
  };

  const hasCbvSuggestion = partnerSuggestions?.some((s) => s.partner_category === 'cbv');

  // Determine CBV widget state from introRequests
  const cbvIntroRequest = introRequests?.find((r) => r.partner_category === 'cbv');
  const cbvWidgetState = cbvIntroRequest
    ? (cbvIntroRequest.status === 'accepted' ? 'connected' : 'pending')
    : (hasAssessment ? 'assessment-context' : 'generic');

  // `bare` = render without AppLayout (used when this component is hosted
  // inside a modal — e.g., OwnerViewModal on the deal team workspace).
  // Default false preserves all existing wizard parents that wrap themselves.
  const Wrapper = bare ? React.Fragment : AppLayout;
  const wrapperProps = bare ? {} : { breadcrumb: 'VALUATION STUDIO > RESULTS' };

  return (
    <Wrapper {...wrapperProps}>
      <div className="max-w-5xl mx-auto px-6 py-12">

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
              VALUATION STUDIO
            </span>
          </div>
          <h1 className="font-serif text-[42px] font-light text-foreground tracking-[-0.02em] mb-2">
            Your Business Valuation
          </h1>
          <p className="text-[16px] text-muted-foreground">
            Industry-specific estimate using EBITDA, revenue, cash flow, and asset methods.
          </p>
        </div>

        {/* ============================================================
            Section 1: Your Business Value (CEPA hero)
            ============================================================ */}
        <div className="bg-card border border-border rounded-[16px] p-8 mb-8">
          {/* Top row */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-2">
              <StatusDot variant="orange" />
              <span className="font-mono text-[11px] uppercase text-muted-foreground/40 tracking-wide">
                VALUATION ESTIMATE
              </span>
            </div>
            {isPrimary ? (
              <span className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                    style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)', color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.2)' }}>
                ★ Active Valuation
              </span>
            ) : (!readOnly && onSetPrimary) ? (
              <button
                type="button"
                onClick={onSetPrimary}
                className="text-[12px] hover:underline transition-colors"
                style={{ color: 'hsl(var(--succeedo-orange))' }}
              >
                Set as active &rarr;
              </button>
            ) : null}
          </div>

          {hasAssessment ? (
            <>
              {/* Hero: single CEPA-adjusted current value. The anchor
                  method (SDE for owner-operated Main Street, EBITDA
                  otherwise) is surfaced inline so the seller can
                  reconcile the headline against the method cards
                  below — same lens that's badged most_reliable. */}
              <div>
                <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-2">
                  Your Current Value
                </div>
                <div className="font-serif text-[48px] font-light text-foreground tracking-[-0.02em] leading-none mb-1">
                  {formatCurrency(heroValue)}
                </div>
                {/* Exact figure + range disclosure. The compact headline above
                    is what owners should anchor on; the full number and the
                    cross-method range live here so the precision and the
                    spread are still visible without dominating the page. */}
                <div className="text-[12px] text-muted-foreground/70 mb-3 font-mono">
                  {formatCurrencyFull(heroValue)}
                  {rangeLow > 0 && rangeHigh > 0 && (
                    <>
                      <span className="mx-2 text-muted-foreground/40">·</span>
                      Range across methods: {formatCurrency(rangeLow)} – {formatCurrency(rangeHigh)}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[14px] text-muted-foreground mb-4 flex-wrap">
                  <span>Based on your</span>
                  <Tooltip text="The Common Sense Score blends 8 categories from your business assessment (financials, customer base, processes, people, growth, etc.) into a single 0-100% readiness rating. Higher scores command higher EBITDA multiples — that's how this score moves your headline number.">
                    <span className="underline decoration-dotted cursor-help">Common Sense Score</span>
                  </Tooltip>
                  <span>of {assessmentScorePct}%</span>
                  <span className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase border ${tierStyle.className}`}>
                    {tierStyle.label}
                  </span>
                </div>
                {va?.anchor && (
                  <div className="text-[12px] text-muted-foreground/70 mb-4">
                    {String(va.anchor) === 'sde'
                      ? 'Anchored on Recasted SDE × Common-Sense-adjusted multiple (Main Street owner-operated lens).'
                      : 'Anchored on Recasted EBITDA × Common-Sense-adjusted multiple.'}
                  </div>
                )}

                {/* Collapsible CTA */}
                <button
                  type="button"
                  onClick={() => setValueGapExpanded(!valueGapExpanded)}
                  className="text-[13px] cursor-pointer flex items-center gap-1 hover:underline"
                  style={{ color: 'hsl(var(--succeedo-orange))' }}
                >
                  See your Value Gap breakdown
                  {valueGapExpanded
                    ? <ChevronUp className="w-4 h-4" />
                    : <ChevronDown className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* Collapsible value gap section */}
              <div
                className="overflow-hidden transition-all duration-300"
                style={{
                  maxHeight: valueGapExpanded ? '500px' : '0',
                  opacity: valueGapExpanded ? 1 : 0,
                }}
              >
                <div className="border-t border-border pt-4 mt-4">
                  {/* Best-in-Class + Value Gap row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <div className="text-[20px] font-light" style={{ color: 'hsl(var(--chart-2))' }}>
                        Best-in-Class Value: {formatCurrencyFull(va.best_in_class_value || 0)}
                      </div>
                      <div className="text-[12px] text-muted-foreground">
                        If your score reaches 72%+
                      </div>
                    </div>
                    <div>
                      <div className="text-[20px] font-light" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                        Value Gap: {formatCurrencyFull(va.value_gap || 0)}
                      </div>
                      <div className="text-[12px] text-muted-foreground">
                        {va.value_gap_pct ? `${(va.value_gap_pct <= 1 ? va.value_gap_pct * 100 : va.value_gap_pct).toFixed(1)}%` : '0%'} improvement potential
                      </div>
                    </div>
                  </div>

                  {/* Range slider: standard orange progress bar */}
                  <div className="mb-6">
                    <div className="relative">
                      <div className="h-2 rounded-full bg-border">
                        <div
                          className="h-full rounded-full bg-[hsl(var(--succeedo-orange))]"
                          style={{ width: `${calculateSliderPosition()}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 mb-2">
                      <span className="font-mono text-[10px] text-muted-foreground/40">
                        {(va.range_low || 0).toFixed(1)}x
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground/40">
                        {(va.range_high || 0).toFixed(1)}x
                      </span>
                    </div>
                    <div className="text-center text-[13px] text-muted-foreground">
                      Your Multiple: {(va.current_multiple || 0).toFixed(1)}x
                    </div>
                  </div>

                  {/* CTA row */}
                  {!readOnly && (
                    <div className="flex items-center justify-between">
                      <a
                        href="/succeedo/action-plan"
                        className="text-[13px] hover:underline"
                        style={{ color: 'hsl(var(--succeedo-orange))' }}
                      >
                        Close your value gap &rarr;
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Fallback: no assessment -- show overall range */}
              <div className="mb-6">
                <div className="font-serif text-[48px] font-light text-foreground tracking-[-0.02em] leading-none mb-3">
                  <AnimatedCurrency value={rangeLow} /> &mdash; <AnimatedCurrency value={rangeHigh} />
                </div>
                <div className="text-[14px] text-muted-foreground">
                  Based on your business profile and financial data
                </div>
              </div>

              {/* CTA to complete assessment */}
              {!readOnly && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[12px] p-5">
                    <div className="text-[16px] text-foreground font-medium mb-2">
                      Unlock Your Value Gap Analysis
                    </div>
                    <p className="text-[13px] text-muted-foreground mb-4">
                      Complete your assessment to see your current value, best-in-class potential, and personalized improvement roadmap.
                    </p>
                    <a
                      href="/succeedo/assessment"
                      className="inline-block text-[13px] font-medium hover:underline"
                      style={{ color: 'hsl(var(--succeedo-orange))' }}
                    >
                      Complete your assessment to unlock your Value Gap analysis &rarr;
                    </a>
                  </div>
                </div>
              )}

            </>
          )}

          {/* Edit Inputs button — hidden in read-only mode */}
          {!readOnly && (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowEditModal(true)}
                className="border border-border text-foreground rounded-full px-5 py-2 text-[13px] font-medium hover:bg-accent transition-colors flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit Inputs
              </button>
            </div>
          )}
        </div>

        {/* ============================================================
            Section 1.5: Method spread + How we got there
            ============================================================
            The four method cards below are alternative LENSES, not competing
            answers. The headline above is one specific calculation (EBITDA ×
            CEPA multiple when an assessment exists, else the cross-method
            average). Without this disclosure, sellers see a 50× spread
            ($225K asset floor → $11.9M DCF) and assume the model is broken.
        */}
        {(rangeLow > 0 && rangeHigh > rangeLow) && (
          <MethodSpreadCard
            rangeLow={rangeLow}
            rangeHigh={rangeHigh}
            heroValue={heroValue}
            hasAssessment={hasAssessment}
            ebitdaMid={ebitda_method?.mid}
            revenueMid={revenue_method?.mid}
            dcfMid={dcf_method?.mid}
            assetMid={asset_method?.mid}
            relevance={rel}
            recastedEbitda={ebitda_method?.adjusted_ebitda}
            currentMultiple={va?.current_multiple}
          />
        )}

        {/* ============================================================
            Section 2: Valuation Methods
            ============================================================ */}
        <div className="flex items-center gap-2 mb-6">
          <StatusDot variant="orange" />
          <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wide">
            VALUATION METHODS
          </span>
        </div>

        {/* Industry + NAICS display. When the benchmark family resolves to
            generic_smb_fallback we hide the slug entirely and replace it
            with a friendlier note — leaking the developer-facing identifier
            erodes trust without giving the seller anything actionable. */}
        {benchmark_info && (() => {
          const family = benchmark_info.benchmark_family;
          const isFallback = family === 'generic_smb_fallback' || benchmark_info.evidence_class === 'broad_fallback';
          const sectorLabel = benchmark_info.sector_cluster?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return (
            <div className="mb-6 flex items-center gap-3 font-mono text-[12px] flex-wrap">
              <span className="text-muted-foreground">INDUSTRY:</span>
              <span className="text-foreground font-medium">{sectorLabel || 'Other'}</span>
              {inputs?.industry_naics_code && (
                <span className="text-muted-foreground/60">NAICS {inputs.industry_naics_code}</span>
              )}
              <span className="text-muted-foreground/40">&middot;</span>
              <span className="text-muted-foreground/60">
                {isFallback
                  ? 'Industry-agnostic estimate (no direct sector match)'
                  : family?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
          );
        })()}

        {/* Method Cards. SDE leads when the business is owner-operated
            (sde_method present); otherwise the existing 4-card 2×2 layout
            renders. With 5 cards present, the grid stretches to 2 cols
            wide × 3 rows tall (with the last row holding a single card). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {sde_method && (
            <MethodCard title="SDE x Multiple" relevance={rel.sde}
              value={sde_method?.mid}
              detail={`Recasted SDE: ${formatCurrency(sde_method.recasted_sde)} x ${parseFloat(sde_method.multiples?.mid || 2.5).toFixed(1)}x`}
              evidence={benchmark_info}>
              {sde_method.is_estimated && (
                <p className="text-[10px] text-muted-foreground/70 mt-2 leading-snug italic">
                  SDE multiple is a blended estimate for this sector cluster.
                  Refined as finer-grained Canadian sector data is incorporated.
                </p>
              )}
              {!sde_method.is_estimated && (
                <p className="text-[10px] text-muted-foreground/70 mt-2 leading-snug italic">
                  Sourced from BizBuySell sector transaction data (US-adjusted for Canadian market).
                </p>
              )}
            </MethodCard>
          )}

          <MethodCard title="EBITDA x Multiple" relevance={rel.ebitda}
            value={ebitda_method?.mid}
            detail={ebitda_method ? `Recasted EBITDA: ${formatCurrency(ebitda_method.adjusted_ebitda)} x ${parseFloat(ebitda_method.multiples?.mid || benchmark_info?.ebitda_multiples?.mid || 4).toFixed(1)}x` : null}
            evidence={benchmark_info} />

          <MethodCard title="Revenue x Multiple" relevance={rel.revenue}
            value={revenue_method?.mid}
            detail={revenue_method ? `Recurring: ${formatCurrency(revenue_method.recurring_revenue)} + Non-recurring: ${formatCurrency(revenue_method.non_recurring_revenue)}` : null}
            evidence={benchmark_info} />

          <MethodCard title="Discounted Cash Flow" relevance={rel.dcf}
            value={dcf_method?.mid}
            detail={dcf_method ? `5-year DCF at ${dcf_method.wacc ? (dcf_method.wacc * 100).toFixed(1) : '15.0'}% WACC` : null}>
            {dcf_method && rel.dcf?.badge !== 'not_applicable' && (
              <div className="mt-2">
                <p className="text-[12px] text-muted-foreground">Growth: {dcf_method.growth_rate ? (dcf_method.growth_rate * 100).toFixed(1) : 0}% ({dcf_method.growth_source === 'historical_cagr' ? 'historical CAGR' : dcf_method.growth_source === 'manual' ? 'manual' : 'default'})</p>
                <button type="button" onClick={() => setShowDCFModal(true)} className="text-[13px] font-medium mt-1 hover:underline" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                  View Projections &rarr;
                </button>
              </div>
            )}
          </MethodCard>

          <MethodCard title="Asset-Based Floor" relevance={rel.asset} value={asset_method?.mid} />
        </div>

        {/* Reported EBITDA <= 0 warning. Triggers when Revenue ≤ OpEx — usually
            a sign the seller entered OpEx that already excluded their salary,
            or fat-fingered Revenue/Expenses. Without this, a $0 reported
            EBITDA collapses Recasted EBITDA down to just the add-back amount,
            which reads like a bug (see partner_dashboard_cleanup discussion). */}
        {ebitda_method && (ebitda_method.reported_ebitda ?? 0) <= 0 && (
          <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/30 rounded-[12px] p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(var(--succeedo-orange))' }} />
            <div className="text-[13px] text-foreground/80 leading-relaxed">
              <span className="font-medium text-foreground">Reported EBITDA is at or below zero.</span>{' '}
              That happens when Operating Expenses match (or exceed) Revenue, so any add-backs become the entire
              Recasted EBITDA. Double-check your Operating Expenses input — it should include your full owner salary
              as reported on the P&amp;L; we add back only the above-market portion separately.
            </div>
          </div>
        )}

        {/* Data Sources — provenance panel for QBO-sourced + manual inputs.
            `data_sources` is server-derived (todo 125): the snapshot persists
            it on `results['data_sources']` so the panel survives a reload. */}
        <DataSourcesPanel inputs={inputs} dataSources={data_sources} />

        {/* Cost Structure — shown when QBO sync provided a COGS breakdown */}
        {inputs?.cogs > 0 && inputs?.revenue > 0 && (() => {
          const cogs = inputs.cogs;
          const opex = Math.max((inputs.operating_expenses || inputs.operatingExpenses || 0) - cogs, 0);
          const grossMargin = inputs.revenue - cogs;
          const grossMarginPct = inputs.revenue > 0 ? (grossMargin / inputs.revenue) * 100 : 0;
          const reportedEbitda = grossMargin - opex;
          return (
            <div className="bg-card border border-border rounded-[12px] p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">COST STRUCTURE</span>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-[14px]">
                  <span className="text-muted-foreground">Revenue</span>
                  <span className="text-foreground font-medium">{formatCurrency(inputs.revenue)}</span>
                </div>
                <div className="flex justify-between text-[14px]">
                  <span className="text-muted-foreground">Cost of Goods Sold</span>
                  <span className="text-foreground font-medium">&minus;{formatCurrency(cogs)}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between text-[14px]">
                  <span className="text-foreground font-semibold">Gross Margin</span>
                  <span className="font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>
                    {formatCurrency(grossMargin)} <span className="font-normal text-muted-foreground">({grossMarginPct.toFixed(1)}%)</span>
                  </span>
                </div>
                <div className="flex justify-between text-[14px]">
                  <span className="text-muted-foreground">Operating Expenses</span>
                  <span className="text-foreground font-medium">&minus;{formatCurrency(opex)}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between text-[14px]">
                  <span className="text-foreground font-semibold">Reported EBITDA</span>
                  <span className="text-foreground font-semibold">{formatCurrency(reportedEbitda)}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recasting Impact */}
        {ebitda_method && ebitda_method.adjustments_total > 0 && (
          <div className="bg-muted border border-border rounded-[12px] p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <StatusDot variant="orange" />
              <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">RECASTING IMPACT</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-[14px]">
                <span className="text-muted-foreground">Reported EBITDA</span>
                <span className="text-foreground font-medium">{formatCurrency(ebitda_method.reported_ebitda)}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span className="text-muted-foreground">Add-backs</span>
                <span className="text-foreground font-medium">+{formatCurrency(ebitda_method.adjustments_total)}</span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between">
                <span className="text-foreground font-semibold">Recasted EBITDA</span>
                <span className="font-semibold" style={{ color: 'hsl(var(--succeedo-orange))' }}>{formatCurrency(ebitda_method.adjusted_ebitda)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Save + Export buttons */}
        {!readOnly && (
          <div className="flex flex-col items-center gap-3 mb-8">
            <div className="flex gap-3">
              <button type="button" onClick={handleSaveSnapshot}
                className={`rounded-full px-6 py-3 font-medium transition-colors ${saveStatus === 'saved' ? 'bg-[hsl(var(--chart-2))] text-white' : 'bg-foreground text-background hover:bg-foreground/90'}`}>
                {saveStatus === 'saved' ? 'Saved \u2713' : saveStatus === 'saving' ? 'Saving\u2026' : 'Save This Snapshot'}
              </button>
              {dealTeam && onShareWithTeam && (
                <button
                  type="button"
                  onClick={onShareWithTeam}
                  aria-label={`Share this snapshot with ${dealTeam.name || 'your deal team'}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  Share with team
                </button>
              )}
              <button type="button" onClick={handleExportPDF} className="border border-border text-foreground rounded-full px-6 py-3 font-medium hover:bg-accent transition-colors flex items-center gap-2">
                <Download className="w-4 h-4" /> Export PDF
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center">
              PDF includes valuation methods, evidence sources, and assessment context
            </p>
          </div>
        )}

        {/* Saved Valuations */}
        {!readOnly && savedSnapshots.length > 0 && (
          <div className="mb-8 pt-8 border-t border-border">
            <div className="flex items-center gap-2 mb-4">
              <StatusDot variant="orange" />
              <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">SAVED VALUATIONS</span>
            </div>
            <div className="space-y-2">
              {savedSnapshots.slice(0, 5).map((snap) => {
                const range = snap.value_range || {};
                const date = snap.created_at ? new Date(snap.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                const sector = (snap.sector_cluster || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                const snapDisplayValue = snap.ebitda_value != null
                  ? formatCurrency(snap.ebitda_value)
                  : `${formatCurrency(range.low || 0)} \u2014 ${formatCurrency(range.high || 0)}`;

                return (
                  <div
                    key={snap.id}
                    className="flex items-center justify-between bg-muted border border-border rounded-[12px] px-4 py-3 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-[13px] text-muted-foreground">{date}</span>
                      {sector && (
                        <span className="font-mono text-[10px] uppercase text-muted-foreground border border-border rounded-full px-2 py-0.5">
                          {sector}
                        </span>
                      )}
                      <span className="text-[14px] font-semibold text-foreground">
                        {snapDisplayValue}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { if (onLoadSnapshot) { onLoadSnapshot(snap); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
                      className="text-[13px] font-medium hover:underline"
                      style={{ color: 'hsl(var(--succeedo-orange))' }}
                    >
                      View
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ============================================================
            Section 3: Next Steps
            ============================================================ */}
        <div className="pt-8 border-t border-border">
          <div className="flex items-center gap-2 mb-6">
            <StatusDot variant="orange" />
            <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wide">
              NEXT STEPS
            </span>
          </div>

          {/* ReadyToConnectWidget: CBV CTA */}
          {!readOnly && hasCbvSuggestion && (
            <div className="mb-8">
              <ReadyToConnectWidget
                partnerCategory="cbv"
                state={cbvWidgetState}
                assessmentScore={hasAssessment ? assessmentScorePct : undefined}
                requestedTime={cbvIntroRequest?.created_at ? new Date(cbvIntroRequest.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : undefined}
                partnerName={cbvIntroRequest?.partner_name}
                partnerFirm={cbvIntroRequest?.partner_firm}
                onRequestIntroduction={() => {
                  if (onRequestIntroduction) onRequestIntroduction('cbv');
                  try { window.ahoy && window.ahoy.track('succeedo_partner_cta_clicked', { partner_category: 'cbv', source: 'valuation_results' }); } catch (e) { /* noop */ }
                }}
                loading={introLoading}
              />
            </div>
          )}

          {/* Featured Partners */}
          {!readOnly && featuredPartners && featuredPartners.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">FEATURED PARTNERS</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {featuredPartners.slice(0, 3).map((p) => (
                  <FeaturedPartnerCard key={p.id} partner={p} />
                ))}
              </div>
            </div>
          )}

          {/* Understanding Your Valuation */}
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Understanding Your Valuation
          </h3>
          <div className="space-y-4 text-sm text-muted-foreground mb-8">
            <div><strong className="text-foreground">EBITDA x Multiple:</strong> The most widely used method for established businesses. We apply an industry-specific multiple (typically 2.5-4.5x) to your adjusted EBITDA.</div>
            <div><strong className="text-foreground">Revenue x Multiple:</strong> A secondary validation method that applies a revenue multiple (typically 0.5-1.5x) based on your industry and growth trajectory.</div>
            <div><strong className="text-foreground">Discounted Cash Flow:</strong> Projects future cash flows and discounts them to present value using your weighted average cost of capital (WACC).</div>
            <div><strong className="text-foreground">Asset-Based Floor:</strong> The conservative baseline representing the liquidation value of your tangible and intangible assets.</div>
          </div>

          {/* Next Step: Deal Structure */}
          {!readOnly && (
            <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5 mt-8">
              <div className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-2">
                NEXT STEP
              </div>
              <h3 className="text-[16px] font-semibold text-foreground mb-2">
                Model your deal structure
              </h3>
              <p className="text-[14px] text-muted-foreground mb-4">
                Now that you know what your business is worth, explore how the deal could be structured — cash, seller financing, earnout, or a blend.
              </p>
              <a href="/succeedo/deal-structure" className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-2.5 text-[14px] font-medium hover:bg-foreground/90 transition-colors">
                Explore Deal Structures <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}

          {/* Back to Dashboard */}
          {!readOnly && (
            <div className="text-center mt-8">
              <a href="/succeedo/home" className="text-[14px] text-muted-foreground hover:text-foreground transition-colors">&larr; Back to Dashboard</a>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && <EditInputsModal inputs={inputs || {}} onClose={() => setShowEditModal(false)} onRecalculate={handleRecalculate} />}
      {showDCFModal && <DCFProjectionModal dcf={dcf_method} onClose={() => setShowDCFModal(false)} />}
    </Wrapper>
  );
}

ValuationSnapshotResult.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  partnerSuggestions: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  savedSnapshots: PropTypes.array,
  onBack: PropTypes.func,
  onRecalculate: PropTypes.func,
  onSaveSnapshot: PropTypes.func,
  onLoadSnapshot: PropTypes.func,
  onRequestIntroduction: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  introRequests: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  featuredPartners: PropTypes.array,
  introLoading: PropTypes.bool,
  isPrimary: PropTypes.bool,
  onSetPrimary: PropTypes.func,
  readOnly: PropTypes.bool,
  bare: PropTypes.bool,
  dealTeam: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
  }),
  onShareWithTeam: PropTypes.func,
};

ValuationSnapshotResult.defaultProps = {
  result: null,
  partnerSuggestions: null,
  savedSnapshots: [],
  onBack: undefined,
  onRecalculate: undefined,
  onSaveSnapshot: undefined,
  onLoadSnapshot: undefined,
  onRequestIntroduction: undefined,
  introRequests: [],
  featuredPartners: [],
  introLoading: false,
  isPrimary: false,
  onSetPrimary: null,
  readOnly: false,
  bare: false,
  dealTeam: null,
  onShareWithTeam: undefined,
};

export default ValuationSnapshotResult;
